import { memo } from '@tanstack/table-core';

import type {
  Row,
  RowData,
  RowModel,
  Table,
} from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';

/**
 * Creates a custom pagination row model.
 *
 * @returns A function that takes a table instance and returns a memoed function to get the paginated row model.
 */
export function getPaginationRowModel<TData extends RowData>(): (
  table: Table<TData>,
) => () => RowModel<TData> {
  return (table) =>
    memo(
      () => [
        table.getState().pagination,
        table.getPrePaginationRowModel(),
        table.options.paginateExpandedRows
          ? undefined
          : table.getState().expanded,
      ],
      (pagination, rowModel) => {
        if (!rowModel.rows.length || table.options.enablePagination !== true) {
          return rowModel;
        }

        const { pageSize, pageIndex } = pagination;
        const { rowsById } = rowModel;
        const pageStart = pageSize * pageIndex;
        const pageEnd = pageStart + pageSize;

        const rows = rowModel.rows.slice(pageStart, pageEnd);

        const paginatedRowModel: RowModel<TData> = {
          rows,
          flatRows: [],
          rowsById,
        };

        // keep track of the already added flatRows, to avoid duplication of already expanded sub-rows
        const addedFlatRowsSet = new Set();

        const handleRow = (row: Row<TData>) => {
          if (addedFlatRowsSet.has(row.id)) {
            return;
          }

          paginatedRowModel.flatRows.push(row);
          addedFlatRowsSet.add(row.id);

          if (row.subRows.length) {
            row.subRows.forEach(handleRow);
          }
        };

        paginatedRowModel.rows.forEach(handleRow);

        return paginatedRowModel;
      },
      getMemoOptions(table.options, 'debugTable', 'getPaginationRowModel'),
    );
}
